(() => {
  const FILTER_PARAM = 'filter.p.m.custom.kleur';
  const HIDDEN_ATTRIBUTE = 'data-splitify-filter-hidden';
  const PRODUCT_LINK_SELECTOR =
    'h3.card__heading a.full-unstyled-link[href*="/products/"][href*="variant="]';
  const COLOR_OPTION_NAMES = new Set(['kleur', 'color', 'colour']);
  const productCache = new Map();
  const unfilteredCardCounts = new Map();

  let scheduledTimer;
  let safetyRevealTimer;
  let runVersion = 0;
  let splitifyGridDetected = false;

  const installStyles = () => {
    if (document.getElementById('splitify-filter-fix-styles')) return;

    const style = document.createElement('style');
    style.id = 'splitify-filter-fix-styles';
    style.textContent = `
      #ProductGridContainer #product-grid {
        transition: opacity 120ms ease;
      }

      #ProductGridContainer.splitify-filter-pending #product-grid {
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  };

  const revealGrid = () => {
    window.clearTimeout(safetyRevealTimer);
    document.getElementById('ProductGridContainer')?.classList.remove('splitify-filter-pending');
  };

  const markGridPending = () => {
    if (!splitifyGridDetected) return;

    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    container.classList.add('splitify-filter-pending');
    window.clearTimeout(safetyRevealTimer);
    safetyRevealTimer = window.setTimeout(revealGrid, 7000);
  };

  const normalize = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase('nl-NL');

  const activeColors = () =>
    [...new URL(window.location.href).searchParams.getAll(FILTER_PARAM)]
      .map(normalize)
      .filter(Boolean);

  const unfilteredContext = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(FILTER_PARAM);
    url.searchParams.delete('sort_by');
    [...url.searchParams.entries()].forEach(([name, value]) => {
      if (!value) url.searchParams.delete(name);
    });
    url.searchParams.sort();
    return `${url.pathname}?${url.searchParams.toString()}`;
  };

  const resetCards = () => {
    document.querySelectorAll(`[${HIDDEN_ATTRIBUTE}]`).forEach((card) => {
      card.hidden = false;
      card.removeAttribute(HIDDEN_ATTRIBUTE);
    });
  };

  const cardIdentity = (card) => {
    const link = card.querySelector(PRODUCT_LINK_SELECTOR);
    if (!link) return null;

    const url = new URL(link.href, window.location.origin);
    const handleMatch = url.pathname.match(/\/products\/([^/]+)/);
    const variantId = url.searchParams.get('variant');

    if (!handleMatch || !variantId) return null;
    return { handle: decodeURIComponent(handleMatch[1]), variantId };
  };

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const productUrl = `${root}products/${encodeURIComponent(handle)}.js`;
      productCache.set(
        handle,
        fetch(productUrl, { credentials: 'same-origin' }).then((response) => {
          if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
          return response.json();
        })
      );
    }

    return productCache.get(handle);
  };

  const colorForVariant = (product, variantId) => {
    const options = Array.isArray(product?.options) ? product.options : [];
    const colorIndex = options.findIndex((option) =>
      COLOR_OPTION_NAMES.has(normalize(typeof option === 'string' ? option : option?.name))
    );

    if (colorIndex < 0) return null;

    const variant = (product.variants || []).find(
      (candidate) => String(candidate.id) === String(variantId)
    );
    if (!variant) return null;

    if (Array.isArray(variant.options)) return variant.options[colorIndex] || null;
    return variant[`option${colorIndex + 1}`] || null;
  };

  const matchesColor = (variantColor, colors) => {
    const normalizedVariantColor = normalize(variantColor);
    return colors.some(
      (color) => normalizedVariantColor === color || normalizedVariantColor.includes(color)
    );
  };

  const applyColorFilter = async () => {
    const colors = activeColors();
    const currentRun = ++runVersion;

    const cards = [...document.querySelectorAll('#product-grid > li')];
    const candidates = cards
      .map((card) => ({ card, identity: cardIdentity(card) }))
      .filter(({ identity }) => identity);

    if (!candidates.length) return;
    splitifyGridDetected = true;

    if (!colors.length) {
      const context = unfilteredContext();
      const expectedCardCount = unfilteredCardCounts.get(context) || 0;

      // When a color is removed, Splitify briefly leaves the previous, smaller
      // variant set in place. Keep waiting until the known complete grid returns.
      if (expectedCardCount && cards.length < expectedCardCount) return;

      unfilteredCardCounts.set(context, Math.max(expectedCardCount, cards.length));
      resetCards();
      revealGrid();
      return;
    }

    const handles = [...new Set(candidates.map(({ identity }) => identity.handle))];
    const productEntries = await Promise.all(
      handles.map(async (handle) => {
        try {
          return [handle, await fetchProduct(handle)];
        } catch (error) {
          console.warn('[Splitify filter fix] Productdata kon niet worden geladen.', handle, error);
          return [handle, null];
        }
      })
    );

    if (currentRun !== runVersion) return;
    const products = new Map(productEntries);

    candidates.forEach(({ card, identity }) => {
      const product = products.get(identity.handle);
      const variantColor = product ? colorForVariant(product, identity.variantId) : null;

      // Fail open when a product or color option cannot be mapped.
      const shouldHide = variantColor ? !matchesColor(variantColor, colors) : false;
      card.hidden = shouldHide;
      if (shouldHide) card.setAttribute(HIDDEN_ATTRIBUTE, 'true');
      else card.removeAttribute(HIDDEN_ATTRIBUTE);
    });

    revealGrid();
  };

  const scheduleApply = () => {
    markGridPending();
    window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => {
      applyColorFilter().catch((error) => {
        console.warn('[Splitify filter fix] Filtercorrectie mislukt.', error);
        revealGrid();
      });
    }, 160);
  };

  const markPendingForFilterEvent = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const changesFacet = target.closest('input[name^="filter."]');
    const removesFacet =
      event.type === 'click' &&
      target.closest('facet-remove a, .active-facets__button, .facets__reset');

    if (changesFacet || removesFacet) markGridPending();
  };

  const observeGrid = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    new MutationObserver(scheduleApply).observe(container, {
      childList: true,
      subtree: true,
    });
  };

  installStyles();

  // Capture facet actions before Dawn starts replacing the collection markup.
  document.addEventListener('change', markPendingForFilterEvent, true);
  document.addEventListener('click', markPendingForFilterEvent, true);

  ['pushState', 'replaceState'].forEach((method) => {
    const original = window.history[method];
    window.history[method] = function (...args) {
      const result = original.apply(this, args);
      scheduleApply();
      return result;
    };
  });

  window.addEventListener('popstate', scheduleApply);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      observeGrid();
      scheduleApply();
    });
  } else {
    observeGrid();
    scheduleApply();
  }
})();
